import type { SessionDto } from "@bafe/api-client";
import { canViewAllHubTasks, type WorkspaceApp } from "@bafe/types";
import { AI_PRODUCT_NAME, SidebarAccount } from "@bafe/ui";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState, type MouseEvent } from "react";
import { ChevronDown, Envelope2, Menu, Search, Sparkles } from "reicon-react";
import { Link, Outlet, useLocation, useMatches, useNavigate } from "react-router";

import { api } from "../lib/api";
import { presentError } from "../lib/errors";
import { isMailOnlyOrganisationRole, WEBMAIL_HOME } from "../lib/mail-only-member.js";
import { webmailInboxUrl } from "../lib/webmail.js";
import { bindHubScrollFade } from "../lib/hub-scroll.js";
import { closeSiblingSelectMenus } from "../lib/select-menu.js";
import { useSession } from "../lib/session";
import { isDarkTheme, useOrganisationStore, useThemeStore } from "../lib/stores";
import { WorkspaceNeeded, needsWorkspace } from "../routes/shell/WorkspacePages";
import { EngageNav, HubNav, MailNav, ManageNav, SiteNav, SocialNav, TeamNav } from "./WorkspaceNav";
import { WorkspaceLogo } from "./WorkspaceLogo";

const APP_LINKS: Array<{ id: "manage" | WorkspaceApp; label: string; to: string }> = [
	{ id: "manage", label: "Manage", to: "/manage" },
	{ id: "hub", label: "Hub", to: "/hub" },
	{ id: "mail", label: "Mail", to: "/mail" },
	{ id: "site", label: "Site", to: "/site" },
	{ id: "team", label: "Team", to: "/team" },
	{ id: "social", label: "Social", to: "/social" },
	{ id: "engage", label: "Engage", to: "/engage" },
];

const APP_LABEL: Record<"manage" | WorkspaceApp, string> = {
	manage: "Manage",
	hub: "Hub",
	mail: "Mail",
	site: "Site",
	team: "Team",
	social: "Social",
	engage: "Engage",
};

function areaOf(pathname: string): "manage" | WorkspaceApp {
	if (pathname === "/hub" || pathname.startsWith("/hub/")) return "hub";
	if (pathname === "/mail" || pathname.startsWith("/mail/")) return "mail";
	if (pathname === "/site" || pathname.startsWith("/site/")) return "site";
	if (pathname === "/team" || pathname.startsWith("/team/")) return "team";
	if (pathname === "/social" || pathname.startsWith("/social/")) return "social";
	if (pathname === "/engage" || pathname.startsWith("/engage/")) return "engage";
	return "manage";
}

function isAiPath(pathname: string) {
	return pathname === "/ai" || pathname.startsWith("/ai/");
}

export function PlatformShell({ session }: { session: SessionDto }) {
	const location = useLocation();
	const navigate = useNavigate();
	const matches = useMatches();
	const queryClient = useQueryClient();
	const organisationId = useOrganisationStore((state) => state.organisationId);
	const setOrganisationId = useOrganisationStore((state) => state.setOrganisationId);
	const theme = useThemeStore((state) => state.preference);
	const setTheme = useThemeStore((state) => state.setPreference);
	const [navOpen, setNavOpen] = useState(false);
	const sideRef = useRef<HTMLDivElement>(null);
	const barRef = useRef<HTMLDivElement>(null);
	const lastArea = useRef<"manage" | WorkspaceApp>("manage");
	const [switchError, setSwitchError] = useState<string | null>(null);

	const title =
		[...matches]
			.reverse()
			.map((match) => (match.handle as { title?: string } | undefined)?.title)
			.find((value) => value) ?? "Key";

	const current = session.memberships.find((membership) => membership.organisationId === organisationId)
		?? session.memberships[0];
	const mailOnlyMember = current ? isMailOnlyOrganisationRole(current.role) : false;
	const pathArea = areaOf(location.pathname);
	if (!isAiPath(location.pathname)) lastArea.current = pathArea;
	const area = isAiPath(location.pathname) ? lastArea.current : pathArea;
	const linkedInSignIn = location.pathname.includes("/engage/linkedin-sign-in/");
	const appLabel = APP_LABEL[area];

	useEffect(() => {
		document.title = `${appLabel} · ${title}`;
	}, [appLabel, title]);

	useEffect(() => {
		const side = sideRef.current;
		const bar = barRef.current;
		if (!side || !bar) return;
		const place = () => {
			const max = side.scrollHeight - side.clientHeight;
			if (max <= 1) {
				bar.classList.remove("is-on");
				return;
			}
			const height = Math.max(28, (side.clientHeight / side.scrollHeight) * side.clientHeight);
			const top = (side.scrollTop / max) * (side.clientHeight - height);
			bar.style.height = `${height}px`;
			bar.style.transform = `translateY(${top}px) scaleX(0.5)`;
			bar.classList.add("is-on");
		};
		place();
		side.addEventListener("scroll", place, { passive: true });
		const resize = new ResizeObserver(place);
		resize.observe(side);
		const mutations = new MutationObserver(place);
		mutations.observe(side, { childList: true, subtree: true });
		return () => {
			side.removeEventListener("scroll", place);
			resize.disconnect();
			mutations.disconnect();
		};
	}, [location.pathname]);

	useEffect(() => {
		let timer = 0;
		let pending: HTMLDetailsElement | null = null;
		const menuOf = (target: EventTarget | null) => {
			if (!(target instanceof Element)) return null;
			const details = target.closest("details.select");
			return details instanceof HTMLDetailsElement ? details : null;
		};
		const onOut = (event: globalThis.MouseEvent) => {
			const details = menuOf(event.target);
			if (!details?.open) return;
			if (details.classList.contains("hub-field-select")) return;
			const next = event.relatedTarget;
			if (!(next instanceof Node)) return;
			if (details.contains(next)) return;
			pending = details;
			window.clearTimeout(timer);
			timer = window.setTimeout(() => {
				if (pending) pending.open = false;
				pending = null;
			}, 160);
		};
		const onOver = (event: globalThis.MouseEvent) => {
			const details = menuOf(event.target);
			if (details && details === pending) {
				window.clearTimeout(timer);
				pending = null;
			}
		};
		const onClick = (event: globalThis.MouseEvent) => {
			const details = menuOf(event.target);
			const picked = event.target instanceof Element ? event.target.closest(".select-pop .select-item") : null;
			if (details && picked) details.open = false;
		};
		document.addEventListener("mouseout", onOut);
		document.addEventListener("mouseover", onOver);
		document.addEventListener("click", onClick);
		return () => {
			window.clearTimeout(timer);
			document.removeEventListener("mouseout", onOut);
			document.removeEventListener("mouseover", onOver);
			document.removeEventListener("click", onClick);
		};
	}, []);

	useEffect(() => {
		setNavOpen(false);
	}, [location.pathname]);

	useEffect(() => {
		const open = () => setNavOpen(true);
		window.addEventListener("bafe:open-nav", open);
		return () => window.removeEventListener("bafe:open-nav", open);
	}, []);

	useEffect(() => {
		document.body.style.overflow = navOpen ? "hidden" : "";
		return () => {
			document.body.style.overflow = "";
		};
	}, [navOpen]);

	const hubTasksBoard =
		area === "hub" &&
		(location.pathname === "/hub/tasks"
			|| location.pathname.startsWith("/hub/tasks/")
			|| location.pathname === "/hub/my/tasks"
			|| location.pathname.startsWith("/hub/my/tasks/"));
	const teamFrame = area === "team" || area === "engage";
	const viewportFrame = teamFrame || hubTasksBoard;

	useEffect(() => {
		if (!viewportFrame) return;
		const root = document.documentElement;
		const sync = () => {
			const viewport = window.visualViewport;
			const height = viewport?.height ?? window.innerHeight;
			const offset = viewport?.offsetTop ?? 0;
			root.style.setProperty("--hub-frame", `${Math.round(height)}px`);
			root.style.setProperty("--hub-frame-offset", `${Math.round(offset)}px`);
			if (offset > 0) window.scrollTo(0, 0);
		};
		const syncAfterKeyboard = (event: FocusEvent) => {
			const target = event.target;
			if (!(target instanceof HTMLElement)) return;
			if (!target.matches("#engage-reply, .team-compose input, .team-compose textarea")) return;
			sync();
			requestAnimationFrame(sync);
			window.setTimeout(sync, 120);
			window.setTimeout(sync, 320);
		};
		sync();
		window.visualViewport?.addEventListener("resize", sync);
		window.visualViewport?.addEventListener("scroll", sync);
		window.addEventListener("resize", sync);
		document.addEventListener("focusin", syncAfterKeyboard);
		return () => {
			window.visualViewport?.removeEventListener("resize", sync);
			window.visualViewport?.removeEventListener("scroll", sync);
			window.removeEventListener("resize", sync);
			document.removeEventListener("focusin", syncAfterKeyboard);
			root.style.removeProperty("--hub-frame");
			root.style.removeProperty("--hub-frame-offset");
		};
	}, [viewportFrame]);

	useEffect(() => {
		if (!navOpen) return;
		const onKey = (event: KeyboardEvent) => {
			if (event.key === "Escape") setNavOpen(false);
		};
		document.addEventListener("keydown", onKey);
		return () => document.removeEventListener("keydown", onKey);
	}, [navOpen]);

	function closeNavFromLink(event: MouseEvent<HTMLElement>) {
		const target = event.target;
		if (target instanceof Element && target.closest("a")) setNavOpen(false);
	}
	const apps = new Set(session.apps ?? []);
	const visibleApps = mailOnlyMember
		? APP_LINKS.filter((item) => item.id !== "manage" && apps.has(item.id))
		: APP_LINKS.filter((item) => item.id === "manage" || apps.has(item.id));
	const viewAllHubTasks = current ? canViewAllHubTasks(current.role, current.viewAllHubTasks === true) : false;
	const showHubStaff = current ? current.role !== "user" : true;

	async function switchWorkspace(nextId: string) {
		setSwitchError(null);
		try {
			const result = await api.selectWorkspace(nextId);
			setOrganisationId(result.organisationId);
			await queryClient.invalidateQueries();
			const here = areaOf(location.pathname);
			if (mailOnlyMember) {
				window.location.assign(WEBMAIL_HOME);
				return;
			}
			if (here !== "manage" && !result.apps.includes(here)) navigate("/manage");
		} catch (error) {
			setSwitchError(presentError(error).title);
		}
	}

	async function signOut() {
		try {
			await api.logout();
		} finally {
			window.location.assign("/signin");
		}
	}

	const hubRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const hub = hubRef.current;
		if (!hub) return;
		return bindHubScrollFade(hub);
	}, []);

	return (
		<div
			ref={hubRef}
			className={`hub${navOpen ? " nav-open" : ""}${viewportFrame ? " hub-viewport-sync" : ""}${teamFrame ? " team-open" : ""}${linkedInSignIn ? " linkedin-signin" : ""}`}
		>
			<button type="button" className="side-mask" aria-label="Close menu" onClick={() => setNavOpen(false)} />
			<aside className="side" onClick={closeNavFromLink}>
				<div className="brand">
					{session.memberships.length > 0 ? (
						<Switcher
							label="Workspace"
							value={current?.organisationName ?? "Workspace"}
							items={session.memberships.map((membership) => ({
								id: membership.organisationId,
								label: membership.organisationName,
								active: membership.organisationId === current?.organisationId,
								onSelect: () => void switchWorkspace(membership.organisationId),
							}))}
						/>
					) : (
						<span className="who">{session.platformOwner ? "Platform" : "No workspace"}</span>
					)}
					<button type="button" className="side-search" aria-label="Search">
						<Search size={16} aria-hidden="true" />
					</button>
				</div>

				<div className="side-scroll" ref={sideRef}>
				<div className="side-bar" ref={barRef} aria-hidden="true" />
				<div className="switchers">
					<Switcher
						label="App"
						value={APP_LINKS.find((item) => item.id === area)?.label ?? "Manage"}
						items={visibleApps.map((item) => ({
							id: item.id,
							label: item.label,
							active: item.id === area,
							to: item.to,
						}))}
					/>
					{switchError && <p className="err">{switchError}</p>}
				</div>

				<nav aria-label="Workspace">
					{area === "manage" && (
						<ManageNav
							platformOwner={session.platformOwner}
							pathname={location.pathname}
							mailOnly={mailOnlyMember}
						/>
					)}
					{area === "hub" && (
						<HubNav
							pathname={location.pathname}
							viewAllTasks={viewAllHubTasks}
							showStaff={showHubStaff}
						/>
					)}
					{area === "mail" && <MailNav />}
					{area === "site" && <SiteNav />}
					{area === "team" && <TeamNav />}
					{area === "social" && <SocialNav />}
					{area === "engage" && <EngageNav />}
				</nav>
				</div>

				<div className="side-account">
					<SidebarAccount
						name={session.user.fullName}
						email={session.user.email}
						settingsHref="/manage/account/profile"
						onSettingsClick={() => navigate("/manage/account/profile")}
						onSignOut={() => void signOut()}
						darkMode={isDarkTheme(theme)}
						onDarkModeChange={(dark) => setTheme(dark ? "dark" : "light")}
					/>
				</div>
			</aside>

			<div className="main">
				<header className="top">
					<button type="button" className="side-toggle" aria-label="Open menu" onClick={() => setNavOpen(true)}>
						<Menu size={18} aria-hidden="true" />
					</button>
					<WorkspaceLogo name={current?.organisationName} logo={current?.logo} />
					<h1 className="top-title">{title}</h1>
					<div className="top-actions">
						<Link className={isAiPath(location.pathname) ? "top-link active" : "top-link"} to="/ai">
							<Sparkles size={16} aria-hidden="true" />
							<span>{AI_PRODUCT_NAME}</span>
						</Link>
						<a className="top-link" href={webmailInboxUrl()}>
							<Envelope2 size={16} aria-hidden="true" />
							<span>Webmail</span>
						</a>
					</div>
				</header>
				<div className="wrap" id="main">
					{session.memberships.length === 0 && needsWorkspace(location.pathname) ? (
						<WorkspaceNeeded platformOwner={session.platformOwner} />
					) : (
						<Outlet />
					)}
				</div>
				<footer className="foot">© {new Date().getFullYear()} Key</footer>
			</div>
		</div>
	);
}

function Switcher({
	label,
	value,
	items,
}: {
	label: string;
	value: string;
	items: Array<{ id: string; label: string; active: boolean; to?: string; href?: string; onSelect?: () => void }>;
}) {
	return (
		<details className="select" onToggle={(event) => closeSiblingSelectMenus(event.currentTarget)}>
			<summary>
				<span>{value}</span>
				<ChevronDown size={12} aria-hidden="true" />
			</summary>
			<div className="select-pop" role="listbox" aria-label={label}>
				{items.map((item) =>
					item.href ? (
						<a key={item.id} className="select-item" href={item.href}>
							{item.label}
						</a>
					) : item.to ? (
						<Link key={item.id} className={item.active ? "select-item active" : "select-item"} to={item.to}>
							{item.label}
						</Link>
					) : (
						<button
							key={item.id}
							type="button"
							className={item.active ? "select-item active" : "select-item"}
							onClick={item.onSelect}
						>
							{item.label}
						</button>
					),
				)}
			</div>
		</details>
	);
}

export function AppGate({ app }: { app: WorkspaceApp }) {
	const session = useSession();
	const label = APP_LINKS.find((item) => item.id === app)?.label ?? app;
	const apps = session.data?.apps ?? [];
	if (session.data && !apps.includes(app)) {
		return (
			<p className="empty">
				{label} is not turned on for this workspace. <Link to="/manage/workspaces">Choose apps in Workspaces.</Link>
			</p>
		);
	}
	return <Outlet />;
}
